import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import normalizeCardPayload from '../../../utils/normalizeCardPayload';
import { isCoordinateLabel } from '../utils/metaUtils';

// Helper to emit toast event instead of blocking alert
const emitToastError = (message: string) => {
  console.error('[usePoiEditing]', message);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('cubemeet-toast-error', { detail: { message } }));
  }
};

export type UploadCardType = 'lodging' | 'restaurant' | 'poi';

export interface UsePoiEditingParams {
  initialEditMode?: boolean;
  normalizedCard?: any;
  card?: any;
  rawData?: any;
  title?: string;
  description?: string;
  address?: string;
  imageUrl?: string;
  tags?: string[];
  metaData?: Array<{ label: string; value: any }>;
  supabaseClient?: any;
  currentUserId?: string;
  // card type hints removed from params; derived dynamically from payload + card objects
  onSaveExternal?: (updated: any) => Promise<any> | void;
  onDeleteExternal?: () => Promise<any> | void;
  onLoadedCanonical?: (card: any) => void;
  // created id is owned by modal; hook can update it through this setter
  createdCardId?: string | null;
  setCreatedCardId?: (id: string) => void;
}

export interface UsePoiEditingResult {
  editMode: boolean;
  setEditMode: (v: boolean) => void;
  saving: boolean;
  localTitle: string;
  setLocalTitle: (v: string) => void;
  localDescription: string;
  setLocalDescription: (v: string) => void;
  localImage?: string;
  setLocalImage: (v?: string) => void;
  localAddress?: string;
  setLocalAddress: (v?: string) => void;
  localMeta: Array<{ label: string; value: any }>;
  setLocalMeta: (v: Array<{ label: string; value: any }>) => void;
  localTags: string[];
  setLocalTags: (v: string[]) => void;
  localBasePayload: any;
  setLocalBasePayload: (v: any) => void;
  basePayloadRef: React.MutableRefObject<any>;
  pendingCoverFile: File | null;
  setPendingCoverFile: (f: File | null) => void;
  patchBasePayload: (patch: Record<string, any>) => void;
  startEdit: () => void;
  save: () => Promise<void>;
  deleteCard: () => Promise<void>;
  onCoverUrl: (url: string) => void;
  onCoverFileSelected: (file: File | null) => void;
  derivedCardType?: string;
  uploadCardType: UploadCardType;
}

export function usePoiEditing(params: UsePoiEditingParams): UsePoiEditingResult {
  const {
    initialEditMode,
    normalizedCard,
    card,
    rawData,
    title,
    description,
    address,
    imageUrl,
    tags,
    supabaseClient,
    currentUserId,
    // removed: uploadCardType, derivedCardType from params
    onSaveExternal,
    onDeleteExternal,
    onLoadedCanonical,
    createdCardId,
    setCreatedCardId,
  } = params;

  const [editMode, setEditMode] = useState<boolean>(!!initialEditMode);
  const [localTitle, setLocalTitle] = useState<string>(title || '');
  const [localDescription, setLocalDescription] = useState<string>(description || '');
  const [localImage, setLocalImage] = useState<string | undefined>(imageUrl || undefined);
  const [localAddress, setLocalAddress] = useState<string | undefined>(address || undefined);
  const [saving, setSaving] = useState<boolean>(false);
  const [localTags, setLocalTags] = useState<string[]>(tags?.slice() || []);
  
  // Initialize localBasePayload from rawData when initialEditMode=true
  const [localBasePayload, setLocalBasePayload] = useState<any>(() => {
    if (initialEditMode && rawData && typeof rawData === 'object') {
      console.log('[usePoiEditing] initial load from rawData:', {
        hasPhone: !!(rawData as any).phone,
        hasWebsite: !!(rawData as any).website,
        hasLocation: !!(rawData as any).location,
        phone: (rawData as any).phone,
        website: (rawData as any).website,
        location: (rawData as any).location,
      });
      return rawData;
    }
    return null;
  });
  
  // Initialize localMeta from rawData.metaPairs, but populate values from extracted fields
  const [localMeta, setLocalMeta] = useState<Array<{ label: string; value: any }>>([]);
  
  // Derive card type early so it can be used in useEffect
  const derivedCardType = useMemo(() => {
    const candidates = [
      (localBasePayload && (localBasePayload.card_type || (localBasePayload as any).type)) || undefined,
      normalizedCard?.card_type,
      normalizedCard?.cardType,
      card?.card_type,
      card?.cardType,
      card?.type,
      rawData?.rawMergedPayload?.card_type,
      rawData?.card_type,
    ];
    for (const c of candidates) { if (typeof c === 'string' && c.trim()) return c; }
    return undefined;
  }, [normalizedCard, card, rawData, localBasePayload]);
  
  // Load metaPairs: either from rawData (OSM) or generate placeholders (QuickAddMenu)
  const hookInstanceId = useRef(`hook-${Date.now()}-${Math.random()}`).current;
  const metaLoadedRef = useRef(false);
  
  useEffect(() => {
    // Skip if already loaded in this hook instance
    if (metaLoadedRef.current) {
      console.log('[usePoiEditing] Skip loading metaPairs, already loaded');
      return;
    }
    
    // Always load rawData if it exists (for both view and edit modes)
    if (rawData && typeof rawData === 'object') {
      // From OSM: load actual data from rawData.metaPairs
      const raw = rawData as any;
      let metaPairs = Array.isArray(raw.metaPairs) ? raw.metaPairs.slice() : [];
      
      console.log('[usePoiEditing] Loading metaPairs from rawData:', {
        hasRawData: !!rawData,
        metaPairsCount: metaPairs.length,
        metaPairsSample: metaPairs.slice(0, 3),
      });
      
      // Populate empty metaPairs with extracted data from rawData
      const phoneVal = raw.phone || '';
      const websiteVal = raw.website || '';
      const priceRangeVal = raw.price_range || '';
      const durationVal = raw.duration_minutes ? String(raw.duration_minutes) : '';
      const openingHoursVal = raw.opening_hours ? (typeof raw.opening_hours === 'string' ? raw.opening_hours : raw.opening_hours.raw || '') : '';
      
      metaPairs = metaPairs.map((pair: any) => {
        const label = (pair.label || '').trim();
        let value = pair.value || '';
        
        // If value is empty or placeholder, populate from extracted data
        if (!value || value === '' || value === '待填写' || value === '待填寫') {
          if (/^电话|電話$/i.test(label)) value = phoneVal;
          else if (/^网站|網站|网络平台|網絡平臺$/i.test(label)) value = websiteVal;
          else if (/^参考价位|參考價位$/i.test(label)) value = priceRangeVal;
          else if (/^建议时长|建議時長$/i.test(label)) value = durationVal;
          else if (/^营业时间|營業時間|开放时间|開放時間$/i.test(label)) value = openingHoursVal;
        }
        
        return { ...pair, value };
      });
      
      console.log('[usePoiEditing] Setting localMeta to:', metaPairs);
      setLocalMeta(metaPairs);
      metaLoadedRef.current = true;
    } else if (initialEditMode && localMeta.length === 0) {
      // From QuickAddMenu: generate placeholder fields based on card type
      const cardKind = (derivedCardType || 'poi').toLowerCase();
      // DISABLED: Do not auto-generate placeholder fields - let users add via bottom buttons
      const placeholders: Array<{ label: string; value: string }> = [];
      
      console.log('[usePoiEditing] NOT generating placeholder metaPairs - users will add fields manually');
      setLocalMeta(placeholders);
      metaLoadedRef.current = true;
    }
  }, [rawData, initialEditMode, derivedCardType, localMeta.length]);
  
  const basePayloadRef = useRef<any>(rawData || null);
  const pendingObjectUrlRef = useRef<string | null>(null);
  const [pendingCoverFile, setPendingCoverFile] = useState<File | null>(null);

  const setLocalBasePayloadSynced = useCallback((updater: any) => {
    setLocalBasePayload((prev: any) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { basePayloadRef.current = next; } catch {}
      return next;
    });
  }, []);

  const clonePayload = (value: any) => {
    if (!value || typeof value !== 'object') return value ?? null;
    try { return JSON.parse(JSON.stringify(value)); } catch {
      if (Array.isArray(value)) return value.slice();
      return { ...value };
    }
  };

  const patchBasePayload = useCallback((patch: Record<string, any>) => {
    if (!patch || typeof patch !== 'object') return;
    setLocalBasePayloadSynced((prev: any) => {
      const next = { ...(prev || {}) };
      Object.entries(patch).forEach(([key, val]) => {
        if (typeof val === 'undefined') delete (next as any)[key]; else (next as any)[key] = val;
      });
      return next;
    });
    if (Object.prototype.hasOwnProperty.call(patch, 'title')) setLocalTitle((patch as any).title || '');
    if (Object.prototype.hasOwnProperty.call(patch, 'description')) setLocalDescription((patch as any).description || '');
    if (Object.prototype.hasOwnProperty.call(patch, 'address')) setLocalAddress((patch as any).address ? String((patch as any).address) : undefined);
    if (Object.prototype.hasOwnProperty.call(patch, 'tags') && Array.isArray((patch as any).tags)) setLocalTags((patch as any).tags.slice());
    if (Object.prototype.hasOwnProperty.call(patch, 'metaPairs') && Array.isArray((patch as any).metaPairs)) setLocalMeta((patch as any).metaPairs.slice());
  }, []);

  const startEdit = useCallback(() => {
    try {
      setEditMode(true);
      setLocalBasePayloadSynced((prev: any) => {
        try {
          if (prev && typeof prev === 'object' && Object.keys(prev).length > 0) {
            console.log('[startEdit] using existing localBasePayload:', {
              hasPhone: !!prev.phone,
              hasWebsite: !!prev.website,
              hasLocation: !!prev.location,
            });
            return prev;
          }
          const payloadCandidates = [
            normalizedCard && normalizedCard.base_payload,
            card && card.base_payload,
            rawData && (rawData as any).base_payload,
            rawData && (rawData as any).rawMergedPayload && (rawData as any).rawMergedPayload.base_payload,
            normalizedCard && normalizedCard.payload,
            card && card.payload,
            // Finally, allow rawData itself when it's a plain object produced by OSM -> payload
            (rawData && typeof rawData === 'object') ? rawData : null,
          ];
          console.log('[startEdit] rawData is:', rawData);
          console.log('[startEdit] checking candidates...', payloadCandidates.map((c, i) => ({ 
            index: i, 
            exists: !!c, 
            isObject: c && typeof c === 'object',
            keys: c && typeof c === 'object' ? Object.keys(c).length : 0,
            hasPhone: c && (c as any).phone,
            hasWebsite: c && (c as any).website,
            hasLocation: c && (c as any).location,
          })));
          let resolvedPayload: any = null;
          for (const c of payloadCandidates) {
            if (c && typeof c === 'object' && Object.keys(c).length) { 
              resolvedPayload = clonePayload(c); 
              console.log('[startEdit] picked candidate, has:', {
                phone: resolvedPayload.phone,
                website: resolvedPayload.website,
                location: resolvedPayload.location,
              });
              break; 
            }
          }
          if (!resolvedPayload) resolvedPayload = {};
          // hydrate critical fields
          try {
            if ((!resolvedPayload.title || resolvedPayload.title === '') && (localTitle && localTitle.length)) resolvedPayload.title = localTitle;
            if ((!resolvedPayload.title || resolvedPayload.title === '') && (title && (title as string).length)) resolvedPayload.title = title;
            if ((!resolvedPayload.description || resolvedPayload.description === '') && (localDescription && localDescription.length)) resolvedPayload.description = localDescription;
            if ((!resolvedPayload.description || resolvedPayload.description === '') && (description && (description as string).length)) resolvedPayload.description = description;
            if ((!resolvedPayload.address || resolvedPayload.address === '') && (localAddress && typeof localAddress === 'string')) resolvedPayload.address = localAddress;
            if ((!resolvedPayload.metaPairs || (Array.isArray(resolvedPayload.metaPairs) && resolvedPayload.metaPairs.length === 0)) && Array.isArray(localMeta) && localMeta.length) resolvedPayload.metaPairs = localMeta.slice();
            if ((!resolvedPayload.tags || (Array.isArray(resolvedPayload.tags) && resolvedPayload.tags.length === 0)) && Array.isArray(localTags) && localTags.length) resolvedPayload.tags = localTags.slice();
          } catch {}
          return resolvedPayload;
        } catch { return prev || {}; }
      });
      // ensure local inputs reflect canonical when empty
      try {
        if ((!localTitle || localTitle.length === 0) && (title || (normalizedCard && normalizedCard.title) || (card && card.title))) {
          setLocalTitle(title || (normalizedCard && normalizedCard.title) || (card && card.title) || '');
        }
        if ((!localDescription || localDescription.length === 0) && (description || (normalizedCard && normalizedCard.description) || (card && card.description))) {
          setLocalDescription(description || (normalizedCard && normalizedCard.description) || (card && card.description) || '');
        }
        if ((!localAddress || (typeof localAddress === 'string' && localAddress.length === 0)) && (address || (normalizedCard && normalizedCard.address) || (card && card.address))) {
          setLocalAddress(address || (normalizedCard && normalizedCard.address) || (card && card.address) || undefined);
        }
      } catch {}
    } catch { setEditMode(true); }
  }, [normalizedCard, card, rawData, localTitle, title, localDescription, description, localAddress, address, localMeta, localTags, derivedCardType]);

  const onCoverUrl = useCallback((url: string) => {
    const bust = (u: string) => {
      try {
        if (/^(blob:|data:)/i.test(u)) return u; // don't bust blobs
        const hasQ = u.includes('?');
        const sep = hasQ ? '&' : '?';
        return `${u}${sep}v=${Date.now()}`;
      } catch { return u; }
    };
    try { setLocalImage(bust(url)); } catch {}
    if (!/^blob:/i.test(url)) {
      try { patchBasePayload({ cover_image: url }); } catch {}
    }
    try {
      if (/^blob:/i.test(url)) {
        if (pendingObjectUrlRef.current && pendingObjectUrlRef.current !== url) {
          try { URL.revokeObjectURL(pendingObjectUrlRef.current); } catch {}
        }
        pendingObjectUrlRef.current = url;
      }
    } catch {}
  }, [patchBasePayload]);

  const onCoverFileSelected = useCallback((file: File | null) => {
    setPendingCoverFile(file);
    // Immediately create and display preview URL
    if (file) {
      try {
        const previewUrl = URL.createObjectURL(file);
        console.log('[usePoiEditing] Created preview URL:', previewUrl);
        onCoverUrl(previewUrl);
      } catch (e) {
        console.error('[usePoiEditing] Failed to create preview URL:', e);
      }
    }
  }, [onCoverUrl]);

  // derive card type from any available payload/card hints (includes localBasePayload when present)
  const uploadCardType = useMemo<UploadCardType>(() => {
    // map derived type to stable subset; fallback by inspecting signals in payload
    const normalizeCardType = (value: any, opts: { fallback: any }) => {
      if (value === 'lodging' || value === 'hotel') return 'lodging';
      if (value === 'restaurant' || value === 'food') return 'restaurant';
      if (value === 'poi' || value === 'place') return 'poi';
      return opts.fallback;
    };
    const mapped = normalizeCardType(derivedCardType, { fallback: null });
    if (mapped) return mapped as UploadCardType;
    const p: any = (basePayloadRef.current || localBasePayload || {});
    const lodgingSignals = !!(p.starRating || p.star || p.hotelName || p.checkInTime || p.checkOutTime || (Array.isArray(p.amenities) && p.amenities.length));
    const restaurantSignals = !!((Array.isArray(p.cuisine) && p.cuisine.length) || p.cuisine || (Array.isArray(p.signatureDishes) && p.signatureDishes.length) || p.signatureDishes || p.duration_minutes);
    if (lodgingSignals) return 'lodging';
    if (restaurantSignals) return 'restaurant';
    return 'poi';
  }, [derivedCardType, localBasePayload]);

  const save = useCallback(async () => {
    if (onSaveExternal) {
      setSaving(true);
      try {
        const sanitizedMeta = Array.isArray(localMeta) ? localMeta.filter((p) => !isCoordinateLabel(p?.label)) : [];
        
        // Merge original payload to preserve embed, siteName, favicon, partner, etc.
        const originalPayload: any = {};
        const payloadCandidates = [
          normalizedCard?.custom_payload,
          normalizedCard?.inline_payload,
          normalizedCard?.payload,
          card?.custom_payload,
          card?.inline_payload,
          card?.payload,
          rawData,
        ];
        for (const candidate of payloadCandidates) {
          if (candidate && typeof candidate === 'object') {
            Object.assign(originalPayload, candidate);
          }
        }
        
        const payloadToSave: any = {
          ...originalPayload, // Preserve all original fields
          title: localTitle,
          description: localDescription,
          address: localAddress,
          tags: localTags,
          cover_image: localImage,
          metaPairs: sanitizedMeta,
          ...(basePayloadRef.current?.coordinates ? { coordinates: basePayloadRef.current.coordinates } : {}),
        };
        await onSaveExternal(payloadToSave);
        setEditMode(false);
      } catch (e) {
        emitToastError(`保存失敗: ${(e as Error).message}`);
      } finally {
        setSaving(false);
      }
      return;
    }

    const activeId = (createdCardId) || (normalizedCard && normalizedCard.id) || (card && (card.id || card.cardId || card.card_id));
    setSaving(true);
    try {
      const resolveUserId = async (): Promise<string | null> => {
        try {
          if (supabaseClient?.auth?.getUser) {
            const { data } = await supabaseClient.auth.getUser();
            if (data?.user?.id) return data.user.id as string;
          }
        } catch {}
        return null;
      };

      let uploadedCoverUrl: string | undefined = undefined;
      if (pendingCoverFile) {
        try {
          const CARD_IMAGE_UPLOAD_ENDPOINT = (typeof window !== 'undefined' && (window as any).__CARD_IMAGE_UPLOAD__) || 'https://card-image-upload.cubemeet.app';
          const form = new FormData();
          form.append('file', pendingCoverFile);
          try { const uid = await resolveUserId(); form.append('userId', uid || ''); } catch { form.append('userId', ''); }
          if (activeId) form.append('cardId', String(activeId));
          if (uploadCardType) form.append('cardType', String(uploadCardType));
          form.append('folder', 'cover');
          const resp = await fetch(CARD_IMAGE_UPLOAD_ENDPOINT, { method: 'POST', body: form, mode: 'cors' as RequestMode });
          let json: any = null; try { json = await resp.json(); } catch {}
          if (!resp.ok) throw new Error((json && (json.error || json.message)) || 'Upload failed');
          const resolvePublicUrl = (payload: any): string | undefined => {
            const primary = payload?.data?.url || payload?.url || payload?.publicUrl || payload?.data?.publicUrl || payload?.Location || payload?.data?.Location;
            const key = payload?.data?.key || payload?.key;
            let u = primary as string | undefined;
            const join = (base: string, path: string) => `${base.replace(/\/$/, '')}/${path.replace(/^\/+/, '')}`;
            if (!u && key) {
              const base = (typeof window !== 'undefined' && ((window as any).__CARD_IMAGE_PUBLIC_BASE__ || (window as any).__R2_PUBLIC_URL__)) || payload?.data?.publicBase || payload?.publicBase || 'https://card-images.cubemeet.app';
              if (base) u = join(String(base), String(key)); else u = String(key);
            }
            if (u && !/^https?:\/\//i.test(u)) {
              const base = (typeof window !== 'undefined' && ((window as any).__CARD_IMAGE_PUBLIC_BASE__ || (window as any).__R2_PUBLIC_URL__)) || payload?.data?.publicBase || payload?.publicBase || 'https://card-images.cubemeet.app';
              if (base) u = join(String(base), String(u));
            }
            return u;
          };
          uploadedCoverUrl = resolvePublicUrl(json);
          if (uploadedCoverUrl) {
            try {
              const sep = uploadedCoverUrl.includes('?') ? '&' : '?';
              setLocalImage(`${uploadedCoverUrl}${sep}v=${Date.now()}`);
            } catch {}
          }
          // Final result snapshot will be logged AFTER persistence once resultCard is available.
        } catch (e) {
          emitToastError('圖片上傳失敗，請稍後重試');
          throw e;
        } finally {
          try { if (pendingObjectUrlRef.current) { URL.revokeObjectURL(pendingObjectUrlRef.current); pendingObjectUrlRef.current = null; } } catch {}
          setPendingCoverFile(null);
        }
      }

      let resultCard: any = null;
      if (supabaseClient) {
        try {
          const client = supabaseClient;
          const fields: any = { title: localTitle, description: localDescription };
          if (typeof localAddress !== 'undefined') fields.address = localAddress && localAddress.trim() ? localAddress : null;
          if (Array.isArray(localTags)) fields.tags = localTags;

          // Priority: use rawData first if available (OSM enriched data), then fall back to existing payloads
          // FIX: Instead of picking just one, we should start with the best "original" source and merge local changes.
          const originalPayloadCandidates = [
            rawData, // ✅ rawData from OSM should be FIRST priority (has phone/website/location)
            normalizedCard && normalizedCard.base_payload,
            card && card.base_payload,
            card && card.card && card.card.base_payload,
            rawData && rawData.rawMergedPayload && rawData.rawMergedPayload.base_payload,
            normalizedCard && normalizedCard.payload && normalizedCard.payload.base_payload,
            normalizedCard && normalizedCard.payload,
            card && card.payload,
          ];
          
          let nextBasePayload: any = {};
          let basePayloadSource: any = null;
          
          // 1. Find best original source
          for (const candidate of originalPayloadCandidates) {
             if (candidate && typeof candidate === 'object') {
                nextBasePayload = clonePayload(candidate);
                basePayloadSource = candidate;
                break;
             }
          }
          
          // 2. Merge local edits (localBasePayload) on top
          // This ensures that if localBasePayload only has { card_type: 'restaurant' }, we keep 'embed' from original source
          const localPayload = basePayloadRef.current || localBasePayload;
          if (localPayload && typeof localPayload === 'object') {
             Object.assign(nextBasePayload, localPayload);
          }

          if (basePayloadSource) { 
            // Log for debugging (basePayloadSource is now just the first candidate found above)
            const source = originalPayloadCandidates.find(c => c && typeof c === 'object');
            console.log('[CardSave] original source has:', {
              phone: source?.phone,
              website: source?.website,
              location: source?.location,
            });
          }
          // Update editable fields, but preserve non-editable ones (phone, website, location, etc.)
          nextBasePayload.title = localTitle;
          nextBasePayload.description = localDescription;
          if (typeof localAddress === 'string') nextBasePayload.address = localAddress;
          
          // Video card fields and other properties are already merged from localPayload above via Object.assign
          
          // Preserve critical fields from basePayloadSource if not already set
          if (basePayloadSource) {
            if (!nextBasePayload.phone && basePayloadSource.phone) nextBasePayload.phone = basePayloadSource.phone;
            if (!nextBasePayload.website && basePayloadSource.website) nextBasePayload.website = basePayloadSource.website;
            if (!nextBasePayload.location && basePayloadSource.location) nextBasePayload.location = basePayloadSource.location;
            if (!nextBasePayload.latitude && basePayloadSource.latitude) nextBasePayload.latitude = basePayloadSource.latitude;
            if (!nextBasePayload.longitude && basePayloadSource.longitude) nextBasePayload.longitude = basePayloadSource.longitude;
          }
          console.log('[CardSave] after preserve, nextBasePayload has:', {
            phone: nextBasePayload.phone,
            website: nextBasePayload.website,
            location: nextBasePayload.location,
          });
          
          if (uploadedCoverUrl) nextBasePayload.cover_image = uploadedCoverUrl;
          if (localImage && !uploadedCoverUrl) {
            const stripQuery = (u: string) => { try { const urlObj = new URL(u); urlObj.search = ''; return urlObj.toString(); } catch { return (u.split('?')[0] || u); } };
            const canonical = (basePayloadRef.current && (basePayloadRef.current as any).cover_image) ? (basePayloadRef.current as any).cover_image : stripQuery(localImage);
            nextBasePayload.cover_image = canonical;
          }
          // 不要自动写入占位图片，如果没有图片就保持为 null/undefined
          if (Array.isArray(localMeta)) {
            nextBasePayload.metaPairs = localMeta;
            
            // Extract structured fields from localMeta and sync to top-level fields
            localMeta.forEach(pair => {
              const label = String(pair.label || '').trim();
              const value = String(pair.value || '').trim();
              
              if (/^电话|電話$/i.test(label) && value) {
                nextBasePayload.phone = value;
              } else if (/^网站|網站|网络平台|網絡平臺$/i.test(label) && value) {
                nextBasePayload.website = value;
              } else if (/^参考价位|參考價位$/i.test(label) && value) {
                nextBasePayload.price_range = value;
              } else if (/^建议时长|建議時長$/i.test(label) && value) {
                const mins = parseInt(value, 10);
                if (!isNaN(mins)) nextBasePayload.duration_minutes = mins;
              } else if (/^营业时间|營業時間|开放时间|開放時間$/i.test(label) && value) {
                nextBasePayload.opening_hours = value;
              }
            });
            
            console.log('[CardSave] extracted from localMeta:', {
              phone: nextBasePayload.phone,
              website: nextBasePayload.website,
              price_range: nextBasePayload.price_range,
              duration_minutes: nextBasePayload.duration_minutes,
              opening_hours: nextBasePayload.opening_hours,
            });
          }
          if (Array.isArray(localTags)) nextBasePayload.tags = localTags;

          if (basePayloadRef.current && typeof basePayloadRef.current === 'object') {
            const latest = basePayloadRef.current;
            const keys = ['website', 'phone', 'price_range', 'duration_minutes', 'opening_hours','starRating','hotelName','amenities','checkInTime','checkOutTime','platform_product_id','bookingTips'];
            for (const k of keys) { if (Object.prototype.hasOwnProperty.call(latest, k)) { (nextBasePayload as any)[k] = latest[k]; } }
          }

          try {
            const { pruned, warnings, size } = normalizeCardPayload(nextBasePayload, derivedCardType as any);
            if (size > 20000) { emitToastError('卡片資料過大，保存被中止，請聯絡開發人員。'); throw new Error('base_payload too large after prune'); }
            const canonicalKeys = ['title','description','address','website','phone','price_range','duration_minutes','opening_hours','tags','cover_image','latitude','longitude'];
            const prunedNoCanon: any = (pruned && typeof pruned === 'object') ? JSON.parse(JSON.stringify(pruned)) : {};
            for (const k of canonicalKeys) { if (Object.prototype.hasOwnProperty.call(prunedNoCanon, k)) delete prunedNoCanon[k]; }
            fields.base_payload = prunedNoCanon;
            try { console.log('[CardSave] normalized base_payload keys', Object.keys(prunedNoCanon)); } catch {}
            if (warnings && warnings.length) { try { console.log('[CardSave] normalize warnings', warnings); } catch {} }
          } catch (e) {
            try {
              const fallback = clonePayload(nextBasePayload) || {};
              const canonicalKeys = ['title','description','address','website','phone','price_range','duration_minutes','opening_hours','tags','cover_image','latitude','longitude'];
              for (const k of canonicalKeys) { if (Object.prototype.hasOwnProperty.call(fallback, k)) delete fallback[k]; }
              fields.base_payload = fallback;
              try { console.log('[CardSave] normalization failed, using fallback base_payload keys', Object.keys(fields.base_payload)); } catch {}
            } catch { fields.base_payload = nextBasePayload; }
          }

          // Extract and persist canonical top-level fields
          // 1) cover_image - 只在有真实图片时才写入
          const coverCandidate = nextBasePayload.cover_image || uploadedCoverUrl || localImage;
          if (coverCandidate && typeof coverCandidate === 'string' && coverCandidate.length > 0) {
            // 排除占位图片
            if (!coverCandidate.includes('placeholders/')) {
              fields.cover_image = coverCandidate;
            }
          }
          // 如果没有真实图片，不写入 cover_image 字段（保持为 null）
          
          // 2) price_range, phone, website, duration, opening_hours
          // Extract from nextBasePayload OR from any available source (rawData, basePayloadRef, etc.)
          const extractField = (key: string): any => {
            if (Object.prototype.hasOwnProperty.call(nextBasePayload, key) && nextBasePayload[key] !== undefined && nextBasePayload[key] !== null && nextBasePayload[key] !== '') {
              return nextBasePayload[key];
            }
            // Fallback: search in all payload sources
            const sources = [rawData, basePayloadRef.current, localBasePayload, normalizedCard, card];
            for (const src of sources) {
              if (src && typeof src === 'object' && Object.prototype.hasOwnProperty.call(src, key) && src[key] !== undefined && src[key] !== null && src[key] !== '') {
                return src[key];
              }
            }
            return undefined;
          };
          
          const phoneVal = extractField('phone');
          if (phoneVal !== undefined) fields.phone = phoneVal;
          
          const websiteVal = extractField('website');
          if (websiteVal !== undefined) fields.website = websiteVal;
          
          const priceRangeVal = extractField('price_range');
          if (priceRangeVal !== undefined) fields.price_range = priceRangeVal;
          
          const durationVal = extractField('duration_minutes');
          if (durationVal !== undefined) {
            const num = Number(durationVal);
            fields.duration_minutes = Number.isFinite(num) ? num : null;
          }
          
          const openingHoursVal = extractField('opening_hours');
          if (openingHoursVal !== undefined) {
            if (openingHoursVal === null || openingHoursVal === '') {
              fields.opening_hours = null;
            } else if (typeof openingHoursVal === 'string') {
              fields.opening_hours = { raw: openingHoursVal };
            } else {
              fields.opening_hours = openingHoursVal;
            }
          }
          
          if (!fields.address && typeof nextBasePayload.address === 'string') fields.address = nextBasePayload.address;

          // 2) latitude/longitude at top-level (for Nearby tab and queries)
          const resolveNumber = (v: any): number | null => { const n = Number(v); return Number.isFinite(n) ? n : null; };
          const resolveCoords = (src: any): { lat: number | null; lon: number | null } => {
            if (!src || typeof src !== 'object') return { lat: null, lon: null };
            const latKeys = ['latitude','lat','y','Lat','Latitude'];
            const lonKeys = ['longitude','lng','lon','x','Long','Longitude'];
            let latVal: number | null = null; let lonVal: number | null = null;
            for (const k of latKeys) { if (Object.prototype.hasOwnProperty.call(src, k)) { const v = resolveNumber((src as any)[k]); if (v !== null) { latVal = v; break; } } }
            for (const k of lonKeys) { if (Object.prototype.hasOwnProperty.call(src, k)) { const v = resolveNumber((src as any)[k]); if (v !== null) { lonVal = v; break; } } }
            // check nested location
            if ((latVal === null || lonVal === null) && (src as any).location && typeof (src as any).location === 'object') {
              const nested = resolveCoords((src as any).location);
              latVal = latVal ?? nested.lat; lonVal = lonVal ?? nested.lon;
            }
            return { lat: latVal, lon: lonVal };
          };
          const coordsCandidates = [nextBasePayload, basePayloadRef.current, localBasePayload, normalizedCard, card, rawData];
          let finalLat: number | null = null; let finalLon: number | null = null;
          for (const c of coordsCandidates) {
            const { lat, lon } = resolveCoords(c);
            if (lat !== null && lon !== null) { finalLat = lat; finalLon = lon; break; }
          }
          if (finalLat !== null && finalLon !== null) {
            fields.latitude = finalLat;
            fields.longitude = finalLon;
          }

          // 3) location JSON (used by city queries). 
          // User requirement: location should ONLY contain administrative fields: { city, country, district, province }
          // NO coordinates, NO formatted_address. Those stay in top-level fields.
          // Extract location from any available source (prioritize rawData which has OSM tags)
          let sourceLoc: any = null;
          const locationSources = [rawData, nextBasePayload, basePayloadRef.current, localBasePayload, normalizedCard, card];
          for (const src of locationSources) {
            if (src && typeof src === 'object' && src.location && typeof src.location === 'object') {
              sourceLoc = { ...src.location };
              break;
            }
          }
          
          if (sourceLoc) {
            const rawLoc = sourceLoc as any;
            const cleanLoc: any = {};
            const cityLike = rawLoc.city || rawLoc.town || rawLoc.village;
            if (cityLike) cleanLoc.city = cityLike;
            if (rawLoc.country) cleanLoc.country = rawLoc.country;
            const districtLike = rawLoc.district || rawLoc.county || rawLoc.suburb || rawLoc.neighbourhood || rawLoc.city_district;
            if (districtLike) cleanLoc.district = districtLike;
            const provinceLike = rawLoc.province || rawLoc.state || rawLoc.region || rawLoc.state_district;
            if (provinceLike) cleanLoc.province = provinceLike;
            
            // Only set location if we have at least one administrative field
            if (Object.keys(cleanLoc).length > 0) {
              fields.location = cleanLoc;
              console.log('[CardSave] extracted location:', cleanLoc);
            }
          }

          // Debug snapshot BEFORE normalization / pruning
          try {
            console.log('[CardSave] pre-normalize snapshot', {
              title: fields.title,
              phone: fields.phone,
              website: fields.website,
              location: fields.location,
              hasMetaPairs: Array.isArray(nextBasePayload.metaPairs) ? nextBasePayload.metaPairs.length : 0,
              sampleMetaPairs: Array.isArray(nextBasePayload.metaPairs) ? nextBasePayload.metaPairs.slice(0, 5) : [],
              locationKeys: fields.location ? Object.keys(fields.location) : [],
              latitude: fields.latitude,
              longitude: fields.longitude,
              cover_image: fields.cover_image || nextBasePayload.cover_image,
            });
          } catch {}

          const nowIso = new Date().toISOString();

          // Ensure DB-safe card_type. Map synonyms and unsupported values to allowed set.
          // Special logic: if no cover_image AND has coordinates, save as 'osm' type (OSM POI with map fallback)
          const sanitizeCardTypeForDB = (input: any): 'poi' | 'restaurant' | 'lodging' | 'shopping' | 'osm' | 'video' => {
            const v = (typeof input === 'string' ? input : '').trim().toLowerCase();

            // 1. Priority: If it's a known semantic type, respect it!
            // This allows "video" or "osm" cards to be categorized as restaurant/lodging etc.
            if (['restaurant', 'lodging', 'shopping', 'activity'].includes(v)) return v as any;
            if (['meal', 'food', 'dining', 'cafe', 'bar', 'bistro', 'kitchen', 'eatery'].includes(v)) return 'restaurant';
            if (['hotel', 'stay', 'accommodation', 'accom', 'inn', 'hostel', 'bnb'].includes(v)) return 'lodging';
            if (['mall', 'store', 'market'].includes(v)) return 'shopping';

            // 2. Check for video type - save as 'video' only if not one of the above
            if (v === 'video' || v === 'external') {
              // Also check if there's an embed object to confirm it's a video card
              if (nextBasePayload.embed || (nextBasePayload.type === 'external')) {
                try { console.log('[CardSave] Detected video card → card_type = video'); } catch {}
                return 'video';
              }
            }
            
            // Check if we have a real cover image (not placeholder, not empty)
            const hasCoverImage = !!(
              (uploadedCoverUrl && uploadedCoverUrl.length > 0) ||
              (localImage && localImage.length > 0 && !localImage.includes('placeholders')) ||
              (nextBasePayload.cover_image && nextBasePayload.cover_image.length > 0 && !nextBasePayload.cover_image.includes('placeholders'))
            );
            
            // Check if we have coordinates (for OSM POIs)
            const hasCoordinates = !!(finalLat !== null && finalLon !== null);

            // Check if we have video embed content
            const hasEmbed = !!(nextBasePayload.embed || (nextBasePayload.type === 'external'));
            
            // If no cover image but has coordinates → this is an OSM POI, save as 'osm' type
            // BUT: If it has video content (embed), do NOT force it to osm, respect the user's choice (e.g. poi)
            if (!hasCoverImage && hasCoordinates && !hasEmbed) {
              try { console.log('[CardSave] No cover image + has coordinates + no embed → card_type = osm'); } catch {}
              return 'osm';
            }
            
            // activity/attraction/sight/place/experience/tour/event/transport -> poi
            return 'poi';
          };
          const rawType = derivedCardType || nextBasePayload.card_type || nextBasePayload.type || uploadCardType || 'poi';
          const dbCardType = sanitizeCardTypeForDB(rawType);
          try { console.log('[CardSave] dbCardType chosen', { rawType, dbCardType, hasCover: !!(uploadedCoverUrl || localImage), hasCoords: !!(finalLat && finalLon) }); } catch {}

          let noOp = false;
          try {
            const existingBase = (basePayloadRef && basePayloadRef.current) ? basePayloadRef.current : null;
            const existingTop = (normalizedCard && typeof normalizedCard === 'object') ? normalizedCard : (card && typeof card === 'object' ? card : {});
            const nextTop = {
              title: localTitle,
              description: localDescription,
              address: typeof localAddress === 'string' ? localAddress : (nextBasePayload && nextBasePayload.address),
              website: nextBasePayload && nextBasePayload.website,
              phone: nextBasePayload && nextBasePayload.phone,
              tags: nextBasePayload && nextBasePayload.tags,
              cover_image: nextBasePayload && nextBasePayload.cover_image,
            };
            const baseChanged = JSON.stringify(existingBase || {}) !== JSON.stringify(nextBasePayload || {});
            const topChanged = ['title','description','address','website','phone','tags','cover_image'].some(k => {
              try { const a = (existingTop as any)?.[k]; const b = (nextTop as any)[k]; return JSON.stringify(a) !== JSON.stringify(b); } catch { return true; }
            });
            if (!baseChanged && !topChanged && !pendingCoverFile) noOp = true;
          } catch {}

          if (noOp) {
            resultCard = normalizedCard || card || (activeId ? { id: activeId } : null);
          } else {
            // Helper: robustly handle unknown columns by retrying without them
            const tryPersist = async (mode: 'update' | 'insert', payload: any): Promise<any> => {
              let working = { ...(payload || {}) } as any;
              const maxAttempts = 5;
              for (let attempt = 0; attempt < maxAttempts; attempt++) {
                try { console.log(`[CardSave] attempt ${attempt + 1}/${maxAttempts} ${mode}`, { keys: Object.keys(working) }); } catch {}
                let res: { data: any; error: any };
                if (mode === 'update') {
                  res = await client.from('cards_cards').update(working).eq('id', activeId).select().single();
                } else {
                  res = await client.from('cards_cards').insert(working).select().single();
                }
                if (!res.error) return res.data;
                const err = res.error;
                const msg = String(err?.message || err?.hint || err?.details || '');
                try { console.warn('[CardSave] persist error', { attempt: attempt + 1, mode, msg }); } catch {}
                // Detect unknown column like: column "location" of relation "cards_cards" does not exist
                const m = msg.match(/column\s+\"?([a-zA-Z0-9_]+)\"?\s+of\s+relation|column\s+\"?([a-zA-Z0-9_]+)\"?\s+does\s+not\s+exist/i);
                const unknownCol = (m && (m[1] || m[2])) ? String(m[1] || m[2]) : null;
                if (unknownCol && Object.prototype.hasOwnProperty.call(working, unknownCol)) {
                  delete (working as any)[unknownCol];
                  try { console.log('[CardSave] removed unknown column and retrying', unknownCol); } catch {}
                  continue; // retry without this column
                }
                // If error due to enum invalid value or type mismatch for optional fields, progressively remove optional fields
                // Optional keys to progressively remove if generic type errors persist (exclude location/cover_image/lat/lon so we KEEP them unless explicitly unknown)
                const optionalKeys = ['price_range','phone','website','duration_minutes','opening_hours','tags','status','card_type','creator_id','visibility'];
                let removed = false;
                for (const k of optionalKeys) {
                  if (Object.prototype.hasOwnProperty.call(working, k)) {
                    delete (working as any)[k];
                    removed = true;
                  }
                }
                if (removed) continue; // try again with minimal set
                throw err;
              }
              throw new Error('Exceeded retry attempts while saving card');
            };

            if (activeId) {
              const updatePayload = { ...fields, updated_at: nowIso };
              const data = await tryPersist('update', updatePayload);
              resultCard = data;
            } else {
              // Ensure creator_id is present (NOT NULL in schema)
              let creatorIdFinal: string | null = currentUserId || null;
              if (!creatorIdFinal) creatorIdFinal = await resolveUserId();
              if (!creatorIdFinal) {
                emitToastError('請先登入後再保存卡片');
                throw new Error('creator_id is required but missing');
              }
              const insertFields: any = { ...fields, card_type: dbCardType, creator_id: creatorIdFinal, status: (fields as any).status || 'active', visibility: 'private', created_at: nowIso, updated_at: nowIso };
              try {
                const data = await tryPersist('insert', insertFields);
                resultCard = data;
              } catch (err: any) {
                const status = Number(err?.status || err?.code || 0);
                const msg = String(err?.message || err?.details || err?.hint || '');
                // RLS: try minimal insert that should satisfy typical policy (creator_id = auth.uid())
                if (/row-level security/i.test(msg) || status === 403) {
                  try { console.warn('[CardSave] RLS fallback triggered'); } catch {}
                  const minimal = { card_type: dbCardType, title: localTitle || nextBasePayload.title || 'Untitled', creator_id: creatorIdFinal };
                  const res = await client.from('cards_cards').insert(minimal).select().single();
                  if (res.error) throw res.error;
                  const newId = res.data?.id;
                  if (newId) {
                    // attempt to update remaining fields (best-effort)
                    const updatePayload = { ...insertFields } as any;
                    delete updatePayload.card_type; delete updatePayload.creator_id; delete updatePayload.created_at;
                    const upd = await client.from('cards_cards').update(updatePayload).eq('id', newId).select().single();
                    resultCard = upd.data || res.data;
                  } else {
                    resultCard = res.data;
                  }
                } else {
                  throw err;
                }
              }
            }
          }
        } catch (e) {
          try {
            const err = e as any;
            const details = err?.message || err?.details || err?.hint || '';
            emitToastError(`保存失敗，請稍後重試\n${details}`);
          } catch {
            emitToastError('保存失敗，請稍後重試');
          }
          throw e;
        }
      } else if (onSaveExternal) {
        await onSaveExternal({ id: activeId || undefined, title: localTitle, description: localDescription, address: localAddress, tags: localTags, metaPairs: localMeta });
      }

      if (resultCard) {
        try {
          console.log('[CardSave] final persisted snapshot', {
            id: resultCard?.id,
            title: resultCard?.title,
            hasBasePayload: !!resultCard?.base_payload,
            basePayloadKeys: resultCard?.base_payload ? Object.keys(resultCard.base_payload) : [],
            metaPairsLen: Array.isArray(resultCard?.base_payload?.metaPairs) ? resultCard.base_payload.metaPairs.length : 0,
            topCover: resultCard?.cover_image,
            topLocationKeys: resultCard?.location ? Object.keys(resultCard.location) : [],
            latitude: resultCard?.latitude,
            longitude: resultCard?.longitude,
          });
        } catch {}
        const resolvedTitle = resultCard.title || (resultCard.base_payload && resultCard.base_payload.title) || localTitle;
        const resolvedDescription = resultCard.description || (resultCard.base_payload && resultCard.base_payload.description) || localDescription;
        const resolvedAddress = resultCard.address || (resultCard.base_payload && resultCard.base_payload.address) || localAddress;
        const resolvedTags = Array.isArray(resultCard.tags) ? resultCard.tags : (resultCard.base_payload && Array.isArray(resultCard.base_payload.tags) ? resultCard.base_payload.tags : localTags);
        const resolvedMeta = resultCard.base_payload && Array.isArray(resultCard.base_payload.metaPairs) ? resultCard.base_payload.metaPairs : localMeta;
        setLocalTitle(resolvedTitle || '');
        setLocalDescription(resolvedDescription || '');
        setLocalAddress(typeof resolvedAddress === 'string' && resolvedAddress.length ? resolvedAddress : undefined);
        if (Array.isArray(resolvedTags)) setLocalTags(resolvedTags.slice());
        if (Array.isArray(resolvedMeta)) setLocalMeta(resolvedMeta.slice());
        if (resultCard.base_payload && typeof resultCard.base_payload === 'object') setLocalBasePayloadSynced(clonePayload(resultCard.base_payload) || {});
        else if (localBasePayload) setLocalBasePayloadSynced(clonePayload(localBasePayload) || {});
        if (!activeId && resultCard.id && setCreatedCardId) { try { setCreatedCardId(String(resultCard.id)); } catch {} }
        if (onLoadedCanonical) { try { onLoadedCanonical(resultCard); } catch {} }
      }
      setEditMode(false);
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  }, [onSaveExternal, localMeta, localTitle, localDescription, localAddress, localTags, localImage, basePayloadRef, createdCardId, normalizedCard, card, rawData, supabaseClient, uploadCardType, currentUserId, derivedCardType, setCreatedCardId, onLoadedCanonical, pendingCoverFile]);

  const deleteCard = useCallback(async () => {
    const activeId = (normalizedCard && normalizedCard.id) || (card && (card.id || card.cardId || card.card_id));
    if (!activeId) return;
    if (!confirm('確認要刪除此卡片嗎？')) return;
    try {
      if (supabaseClient) {
        const client = supabaseClient;
        const { error } = await client.from('cards_cards').delete().eq('id', activeId);
        if (error) throw error;
        if (onDeleteExternal) await onDeleteExternal();
      } else if (onDeleteExternal) {
        await onDeleteExternal();
      }
    } catch {
      emitToastError('刪除失敗，請稍後重試');
    }
  }, [normalizedCard, card, supabaseClient, onDeleteExternal]);

  return {
    editMode,
    setEditMode,
    saving,
    localTitle,
    setLocalTitle,
    localDescription,
    setLocalDescription,
    localImage,
    setLocalImage,
    localAddress,
    setLocalAddress,
    localMeta,
    setLocalMeta,
    localTags,
    setLocalTags,
    localBasePayload,
    setLocalBasePayload: setLocalBasePayloadSynced,
    basePayloadRef,
    pendingCoverFile,
    setPendingCoverFile,
    patchBasePayload,
    startEdit,
    save,
    deleteCard,
    onCoverUrl,
    onCoverFileSelected,
    derivedCardType,
    uploadCardType,
  };
}
